import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const reframeClipSkill: Skill = {
    name: SKILL_NAMES.reframeClip,
    description:
        'Load BEFORE reframing a clip cut from a recording for an aspect ratio: making it vertical, square or 4:5 for shorts, reels, TikTok or feeds, or cleaning up its framing in landscape. Covers one fixed camera, footage that cuts between people, fully produced footage with several angles and b-roll, galleries and video calls, and showing several speakers at once in a split or stack.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Reframe a Clip
Reframing decides what part of the recording's picture each moment of a clip
shows, for the shape the clip will be posted in. It only ever happens to a
**clip**: a talking-head scene group already cut from a recording. Choosing the
clip is not part of this skill.

## Before you start
- **There must be a clip.** Find it with \`${TOOL_NAMES.getVideoState}\`: a
  talking-head scene group playing a part of a recording, at most 10 minutes
  long. If there isn't one, don't reframe anything: the clip is cut first,
  with \`${TOOL_NAMES.addTalkingHeadSceneGroup}\` and the clip's start_quote and
  end_quote. Never reframe a whole long recording.
- **Reframing comes first after the cut.** Do it before captions, text,
  b-roll or any other scene. Everything placed later is placed around the
  faces this step puts on screen.
- **Do only the framing.** Don't change what the clip says, don't clean up the
  recording, and don't add captions, text or music unless the user asked for
  that too. When this runs as part of a bigger build (a clip recipe), that
  build fills in its text, b-roll and captions after the framing, from the
  overlay plan below.
- **Know the target aspect ratio.** \`landscape\` (16:9), \`vertical\` (9:16),
  \`fourfive\` (4:5) or \`square\` (1:1). If the user named a platform, that
  decides it: shorts, reels and TikTok are \`vertical\`; feed posts are
  \`fourfive\` or \`square\`; YouTube and the web are \`landscape\`. If nothing
  says, ask with \`${TOOL_NAMES.askUserQuestion}\`, with \`vertical\` first.
- **Set the aspect ratio before anything else** with \`${TOOL_NAMES.setAspectRatio}\`,
  if the video isn't already at it. Designs, crops and face positions all
  depend on it, and changing it later means reframing every scene again.

## 1. Look at the clip
1. \`${TOOL_NAMES.listSceneDesigns}\` with narrative_thread="talking_head", now
   that the aspect ratio is set. Note the lower third and callout designs, and
   the designs with two or three media slots ("Multi-speaker layouts"): they
   show several people at once. The design marked as the base scene can't be
   added as a scene. Section 4 says which design each scene uses.
2. \`${TOOL_NAMES.analyzeClipFraming}\` on the clip's scene group. It returns the
   **shot table**, the **speaker scenes to add**, the **b-roll stretches** and
   the **contact sheet**:
   - Each shot with people in it has a **camera angle** letter. Shots with the
     same letter frame the same people the same way; a cut between two of them
     is a **jump cut**, which doesn't change the framing.
   - Shots with no face, and brief flashes of an angle neither neighbour
     shares, are **b-roll**.
   - Each shot lists its faces, numbered left to right as frame_speaker
     numbers them.
   - "Speaker scenes to add" groups consecutive shots on the same angle into
     one scene and gives the exact words each one covers.
   - "B-roll stretches" are the parts that get no scene: they play under the
     base scene's centre crop.
   - The contact sheet shows each shot's frame with the faces boxed and
     numbered.
   Don't use \`${TOOL_NAMES.analyzeVideo}\` for this: it samples the whole
   recording and is far too coarse for a clip.
3. **Write a cast list in your message** before planning: each person in the
   clip, how they look ("woman in the red jacket"), their name if the recording
   or the user gives it, and which angle and face number they are. In edited
   footage the same face number means a different person from angle to angle:
   in a close-up, whoever is on screen is face 1.
4. Read the table's first line: it says whether the clip is **one camera
   angle** (a fixed camera), **edited** (changes of angle between the people),
   or **produced** (changes of angle plus shots with no face: b-roll, slides,
   graphics). The approach below follows from that.

## 2. How much of the picture fits
A full-frame slot shows this share of a 16:9 recording's width at scale 100
(the share of its height is always all of it):

| Target | Width shown |
|---|---|
| landscape | all of it |
| fourfive | 45% |
| square | 56% |
| vertical | 32% |

At a higher \`scale\` it shows less: divide by scale ÷ 100. For a recording of
another shape, the share is (target width ÷ height) ÷ (recording width ÷
height), capped at 100%. A slot of a multi-slot design has its own shape: a
vertical split stacks two slots that are each about as wide as they are tall,
so each shows about 63% of a 16:9 recording's width.

**Two people fit in one crop** when the span from the left edge of the left
face to the right edge of the right face, plus about a face's width on each
side for shoulders, is less than the width shown. The shot table gives each
face's position and size as fractions of the picture.

## 3. Plan the scenes
**When the clip keeps the recording's shape** (a landscape recording in a
landscape video), the picture already fits: the base scene shows it all. Add
scenes only on purpose: a push-in on a key line, a split to show a reaction,
or a crop onto the subject of a b-roll shot. Everything below is about
changing shape.

**A vertical recording going wider** (to \`landscape\`, \`square\` or
\`fourfive\`): the picture can't fill the frame without cutting off heads and
chins. Use \`framing: "fit"\` inside a design that fills the rest of the frame,
or a multi-slot design that pairs the person with b-roll. Never stretch it,
and never leave black bars at the sides.

Otherwise **start from the table's speaker scenes: one scene per run of the
same camera angle, and nothing for b-roll.** The crop changes only when the
angle does. A jump cut
within an angle keeps its crop, so never split a scene at one: every extra
scene on the same person reframes them slightly differently, and the picture
stutters. Split an angle's scene further only for a reason below (a two-shot
cut by speaking turn, a face that moves). Then decide each scene's treatment:

- **One person in the shot:** a speaker scene (section 4), framed on that face
  with \`frame_speaker\`. In landscape, the base scene's full frame is already
  right; add a scene only for a deliberate push-in.
- **Two or three people in one shot:** the shot table's spacing line decides.
  - **Close together** (walking side by side, standing or sitting next to each
    other, sharing the frame): **never split or stack them.** A split cuts
    between two people who belong together and repeats their surroundings in
    each slot. Keep them in one crop centred between them, computed by hand
    (section 5); trimming their outer shoulders is fine. If even both faces
    can't fit in one crop, frame whoever is talking instead, as below.
  - **Far apart** (an interview two-shot with space between them, separate
    seats across a table): if they fit in one crop (section 2), one crop
    centred between them. Otherwise a split or stack from "Multi-speaker
    layouts", one person per slot (\`frame_speaker\` 1 in one slot, 2 in the
    next): side by side in landscape, stacked in vertical. Use it when they
    talk over each other, react, or trade short lines.
  - Or, close or far, cut between them: a speaker scene per speaking turn,
    each framed on whoever is talking. Use it when they take long turns that
    the transcript makes clear (questions and answers).
  - The transcript doesn't say who is talking. For far-apart people, when you
    can't tell from the words, use the split or stack: it is right whoever is
    speaking. For people close together, use the one crop holding both.
- **A gallery, video call or audience (four or more faces):** a speaker scene
  per speaking turn framed on the active speaker's tile, with multi-slot
  scenes pairing the speaker with listeners where the reaction matters. Keep
  frame_speaker's own close-up (no \`scale\`, or a higher one): a lower scale
  shows the neighbouring tiles, their labels and the black space between them.
- **B-roll (no face, or a brief flash):** add no scene. It plays under the
  base scene, whose crop stays a centre fill: one crop for the whole stretch,
  however many quick shots it has. A speaker crop would show it zoomed in on
  some corner of the picture.
  - Add a scene for a b-roll shot only when the centre crop loses what
    matters: a slide, a chart, a screen or a title card that must stay whole
    (\`framing: "fit"\` inside a design that fills the rest of the frame, never
    black bars), or a subject far off-centre (a crop moved onto it by hand,
    section 5).
- **A shot where faces move or people come and go** (the table says so):
  split it into two or three scenes at word boundaries near the changes, each
  framed on its own part. frame_speaker frames each scene at its own middle,
  so this follows the movement. If one looser crop holds every position the
  table lists, that works too.
- **A shot with no words** can't have a scene of its own, because scenes start
  and end on words. It plays under whichever scene covers that moment. That is
  fine for a second or so.
- **Don't split for length.** A speaker scene with no text can hold as long as
  its angle does, however many words it covers.

**Which people to show:**
- **One fixed camera:** whoever is talking is always on screen. A listener
  appears alongside the speaker in a multi-slot design, never instead of them.
- **Edited or produced footage:** trust the edit. The editor already chose
  who to show, including cutaways to a listener or to b-roll; frame what each
  shot shows.
- **A split or stack needs both people in the same shot, each with room of
  their own.** Every slot plays the same moment of the recording, so in
  footage that only ever shows one person at a time there is no split to
  make: cut with the edit instead. Video-call tiles always have room of their
  own; people sharing the frame in natural footage usually don't.

**When there is an overlay plan** (the build planned lower thirds, a callout
or b-roll before reframing), build the speaker scenes around it:
- A planned lower third or callout: a speaker scene on an empty lower-third
  or callout design covering exactly its words, framed like the rest of its
  angle. Split the angle's scene around it; the crop stays the same on both
  sides, because frame_speaker reuses the angle's crop.
- Planned b-roll: leave its words out of every speaker scene. The build adds
  the cutaway there afterwards.
- Don't fill in the text or the b-roll here. Frame first; the build fills
  them in once every scene is framed.

**Write the plan in your message** as a list, one line per scene: the shots
and angle it covers, its first and last words, the design, and for each media
slot the face number (from the shot table) and who that is from the cast list.
List the b-roll stretches that stay on the base scene too.

## 4. Build the scenes
**Which design a speaker scene uses:**
- **By default, a copy of the base scene:** pass the base scene's scene_id as
  the scene's \`scene_design_id\`. The copy already shows the speaker, linked to
  the recording, ready for frame_speaker.
- **Where a lower third or callout will go later:** a lower third or callout
  design with its text set to empty (\`${TOOL_NAMES.setText}\` with \`text: ""\` on
  each text asset). It shows the speaker, and with no text its bar, box and
  other decoration disappear too, so it reads as a plain speaker crop until
  the text is filled in.
- **Several people at once:** a design from "Multi-speaker layouts", one
  person per slot. Those are built for speakers: their slots fill the frame,
  a split side by side or a stack. "Other multi-image layouts" are photo
  layouts with text areas, shapes and gaps; use one only when no multi-speaker
  layout fits, and never switch between designs to chase a better picture:
  pick the multi-speaker layout once.
- **Never the cutaway design for a speaker scene.** It is for real cutaways:
  its media slot starts on the design's placeholder footage, not the speaker.

**Placeholder footage is not broken media.** A media slot that frame_speaker
hasn't framed yet may show the design's placeholder clip in the video state
(\`<unregistered>\`). frame_speaker links it to the recording. Frame a scene
before judging it, and never delete scenes because of what an unframed slot
shows.

- Add every scene in **one** \`${TOOL_NAMES.addVoiceoverScene}\` call, each with the
  words the table's speaker scenes list for it, copied exactly, pause tags
  included.
  If a scene is rejected because a word doesn't match, copy its words again
  from the table and retry that scene: never leave a planned scene out. The scenes tile the clip; the base scene only shows under words
  no scene covers.
- **Never \`${TOOL_NAMES.splitSceneGroup}\`** and never add a second scene group: the
  clip stays one scene group, and its scenes are how the picture changes.
- If the scene group already has scenes: keep ones that carry text or other
  media, and replace speaker scenes from an earlier framing (delete them, then
  build from the shot table).

## 5. Frame each scene
Load \`${SKILL_NAMES.adjustMedia}\` before the first \`${TOOL_NAMES.editMedia}\`. Each scene
has one fixed crop: no animated zoom, no tracking. The crop changes only at a
scene boundary, which is why scenes follow the camera angles.

**People: \`frame_speaker\`.** \`${TOOL_NAMES.editMedia}\` with \`frame_speaker\` crops in
close on one detected face, working out scale and position for you. It looks
at the frame at the middle of the scene, so in a scene that covers one angle,
its face numbers are the shot table's for that angle. Its result says where the face it
framed sits ("at the top right") and where the others are: if that isn't the
person the plan meant, frame again with the right number.
- Pass \`scale\` in the same call for a looser or tighter shot (changing
  \`scale\` on its own afterwards drifts off the face). For \`fourfive\` and
  \`square\`, a slightly looser shot than the default often reads better.
- It works on any media slot in the scene: it links the slot to the
  recording, so every slot plays the same moment in sync. **Never
  \`${TOOL_NAMES.setMedia}\` the recording into a slot**; that copy plays out of sync.
- **Its crop check is the answer on the crop.** Every frame_speaker result
  ends with a crop check worked out from the crop and the detected faces: the
  face whole inside the slot and the recording covering the whole slot (so no
  bars or empty space are possible), or what is wrong. Trust it over any
  description of a render. A clean check needs no nudging, in a single slot
  or in a split or stack.
- When it says the scene plays across a change of camera angle or into
  b-roll, the scene doesn't match the table: fix its words so each angle has
  its own scene and b-roll stays uncovered, then frame again.
- **One crop per face per angle.** Once a face on an angle has been framed,
  frame_speaker on a later scene of that angle and face (after b-roll, or when
  the camera comes back, or a scene split off for a headline) reuses the same
  crop, and says so, so the picture doesn't jump. Passing \`scale\` frames it
  afresh; do that only to change the shot on purpose.
- Its "no face found" error means the footage at that point has no one in it:
  treat the scene as a no-face shot.

**Computing a crop by hand** (two people in one crop, b-roll, a slide). Don't
search for it by nudging. Read off the recording's picture, as fractions from
its top-left corner:
- \`fx\`, \`fy\`: where the crop's centre should be (for two people, halfway
  between their face centres).
- \`h\`: how much of the picture's height the subject fills (1 for a
  full-frame shot, about 0.5 for a side-by-side call tile).

**Write \`fx\`, \`fy\` and \`h\` in your message, then the numbers you get**,
before the \`${TOOL_NAMES.editMedia}\` call:
- \`scale\` = 100 ÷ \`h\`, plus 10-30 for a tighter shot.
- \`position.x\` = (\`fx\` − 0.5) × the media's width at that scale, and
  \`position.y\` = (\`fy\` − 0.5) × its height, rounded and kept a pixel inside
  \`mediaXTranslationRange\` / \`mediaYTranslationRange\`. The video state's
  \`mediaResolution\` is the media's size at its current scale: divide by the
  current scale and multiply by the new one.
- A crop computed by hand has no crop check, so confirm it with one
  \`${TOOL_NAMES.imageAsk}\` asking where the subject sits, then nudge: a subject
  **right** of centre needs a **more positive** \`position.x\`.

**Rules that hold for every scene:**
- **The base scene stays a centre fill** (\`framing: "fill"\`): it plays the
  b-roll. Never frame it on a person. Every other scene that shows the
  recording has its own crop.
- **Match a framing by copying numbers, never by eye.** To frame a scene like
  another of the same person and shot, copy that scene's \`scale\` and
  \`position\` from the video state, or run frame_speaker again. An
  \`${TOOL_NAMES.imageAsk}\` answer's pixel figures describe a small preview, not
  \`position\` units.
- **A crop belongs to the footage it was framed for.** After
  \`${TOOL_NAMES.setSceneRange}\` or \`${TOOL_NAMES.deleteScene}\`, footage moves under crops
  framed for something else: frame every scene whose words changed again.
- **Burned-in labels, captions, logos and call UI** get cut by close crops.
  That is the cost of a close crop, not a defect: never move a crop off a face
  to keep one whole.
- A close-up cuts props at the frame edge (a microphone, a desk, hands). That
  is fine. Never move the crop away from the face to fix it.

## 6. Check every scene
- **The crop is already checked.** frame_speaker's crop check says whether the
  face is whole and the slot covered. Don't ask a vision model about bars,
  empty space or cut-off faces on a crop that passed it: a render description
  can mistake a split's seam or a design's background for a bar, and the same
  crop can get a different answer each time.
- **Ask \`${TOOL_NAMES.imageAsk}\` only who is on screen**, one question per scene:
  does it show the person the plan meant (described by how they look, from
  the cast list)? Never ask again about a crop you haven't changed.
  \`${TOOL_NAMES.imageAsk}\` sees the scene as cropped, so "no person" means the crop
  is wrong, not that the footage is empty: frame again before concluding
  anything.
- **Never delete and re-add a scene to retry it.** The same design with the
  same numbers gives the same picture.
- **Defects:** a face cut off at the frame edge, a crop of mostly furniture,
  wall or empty tile, black bars or a letterboxed picture, the wrong person in
  a scene, a slot showing two people's halves, a scene that plays across a
  change of camera angle, and the crop jumping between scenes on the same
  angle of the same person.
- If the clip has captions, put them in the space the framing leaves, clear
  of faces, with \`${TOOL_NAMES.setCaptions}\` \`position\` (only \`position\`). In a
  stack, the clear space is the seam: the line across the middle of the frame
  where the two slots meet, between the two faces. Captions at the bottom of
  a stack sit on the lower speaker's chin.

## When the user corrects you
Do what they say first: they are looking at the actual video. "Make it match
that scene" means copy that scene's values exactly.

## Finishing
Say in a few sentences what the clip is (fixed, edited or produced), the
target aspect ratio, and how you framed it: which stretches are close-ups,
splits or stacks, and what you did with any b-roll or slides. Describe only
what you checked is on screen.`;
    },
};
